import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import {
  PageHeader, Panel, Kpi, Meter, StatusLight, Chip, Banner,
  AgentPortrait, DsButton, ApprovalCard, SpendChart,
} from "../ds";
import { DecisionDialog } from "../components/DecisionDialog";
import { fmtUSD, fmtRelative } from "../lib/format";
import { toast } from "sonner";

function statusKey(agent) {
  if (!agent.online) return "offline";
  if (agent.status === "blocked") return "blocked";
  if (agent.has_recent_activity) return "working";
  return "idle";
}

export default function Dashboard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { usdPerEcu } = useBilling();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
    refetchInterval: 15000,
  });
  const { data: approvalsData } = useQuery({
    queryKey: ["approvals"],
    queryFn: async () => (await api.get("/tasks", { params: { state: "plan_ready", limit: 10 } })).data,
    refetchInterval: 15000,
  });
  const { data: tasksData } = useQuery({
    queryKey: ["tasks-running"],
    queryFn: async () => (await api.get("/tasks", { params: { limit: 200 } })).data,
    refetchInterval: 15000,
  });
  const { data: costSeries } = useQuery({
    queryKey: ["costs-series"],
    queryFn: async () => (await api.get("/costs", { params: { by: "project", window: "month" } })).data,
    refetchInterval: 30000,
  });

  const [decisionOpen, setDecisionOpen] = React.useState(false);
  const [decision, setDecision] = React.useState("approve");
  const [activeId, setActiveId] = React.useState(null);
  const trigger = (id, d) => { setActiveId(id); setDecision(d); setDecisionOpen(true); };

  if (isLoading && !data) return <div className="p-10 text-[color:var(--muted)]">Loading…</div>;

  const counts = data?.counts || {};
  const costs = data?.costs || { today: {}, month: {} };
  const agents = data?.agents || [];
  const budgetUsd = costs?.today?.budget_usd || 0;
  const spentUsd = costs?.today?.total_usd || 0;
  const budgetBlocked = data?.budget_blocked || [];

  const onlineCount = agents.filter((a) => a.online).length;
  const waiting = counts.pending_approvals ?? 0;
  const oldest = (approvalsData?.tasks || []).reduce((min, t) => {
    const ts = t.updated_at ? new Date(t.updated_at).getTime() : Infinity;
    return Math.min(min, ts);
  }, Infinity);
  const oldestLabel = oldest !== Infinity ? fmtRelative(new Date(oldest).toISOString()).replace(" ago", "") : "—";

  // 7-day series from /costs?window=month
  const spendSeries = (costSeries?.series || []).slice(-7).map((s) => ({
    day: s.day,
    total: Number(s.usd || 0) + Number(s.ecu || 0) * (usdPerEcu || 0),
    claude: Number(s.usd || 0),
  }));

  const running = (tasksData?.tasks || []).filter((t) =>
    ["building", "emergent_testing", "acceptance_testing", "fix_requested"].includes(t.state)
  );

  const firstName = (user?.email || "").split("@")[0].split(".")[0];
  const greet = `Good day, ${firstName.charAt(0).toUpperCase() + firstName.slice(1)}`;

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-6" data-testid="dashboard">
      <PageHeader
        title={greet}
        subtitle={`${waiting} plan${waiting === 1 ? "" : "s"} wait for your approval · ${counts.inbound_new ?? 0} new contact request${counts.inbound_new === 1 ? "" : "s"}`}
      />

      {budgetBlocked.length > 0 && (
        <Banner
          testId="budget-banner"
          action={
            <DsButton variant="default" onClick={() => navigate("/settings")} data-testid="budget-review">
              Review limits
            </DsButton>
          }
        >
          {budgetBlocked.map((b, i) => (
            <span key={b.agent_name} data-testid={`budget-blocked-${b.agent_name}`}>
              <span className="font-semibold">{b.agent_name}</span> stopped: {b.limit_hit.replace(/_/g, " ")}
              {i < budgetBlocked.length - 1 && " · "}
            </span>
          ))}
        </Banner>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4" data-testid="kpi-row">
        <Kpi
          label="AGENTS ONLINE"
          value={`${onlineCount}`}
          sub={`${agents.length} total`}
          right={<span className="font-mono text-xs text-[color:var(--muted)]">/ {agents.length}</span>}
          testId="kpi-agents"
        />
        <Kpi
          label="WAITING FOR YOU"
          value={<span style={{ color: waiting > 0 ? "var(--warn)" : "var(--fg)" }}>{waiting}</span>}
          sub={waiting > 0 ? `oldest ${oldestLabel}` : "all clear"}
          testId="kpi-pending"
        />
        <Kpi
          label="AGENT SPEND TODAY"
          value={fmtUSD(spentUsd)}
          sub={<Meter value={spentUsd} max={budgetUsd || 1} testId="daily-meter" />}
          right={<span className="font-mono text-xs text-[color:var(--muted)]">/ {fmtUSD(budgetUsd)}</span>}
          testId="kpi-spend"
        />
        <Kpi
          label="EMERGENT THIS MONTH"
          value={<>{Number(costs.month?.ecu || 0).toFixed(0)} <span className="text-sm text-[color:var(--muted)]">ECU</span></>}
          sub={`~${fmtUSD((costs.month?.ecu || 0) * (usdPerEcu || 0))}`}
          right={<span className="font-mono text-xs text-[color:var(--muted)]">${(usdPerEcu || 0).toFixed(3)}/ECU</span>}
          testId="kpi-ecu"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Approval queue */}
        <Panel
          label="APPROVAL QUEUE"
          action={
            <span className="font-mono text-[10.5px] text-[color:var(--muted)]">
              {approvalsData?.tasks?.length || 0} waiting
            </span>
          }
          testId="approval-queue"
          className="col-span-1 space-y-3"
        >
          {(approvalsData?.tasks || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-[color:var(--muted)]">
              Inbox zero. No plans waiting.
            </div>
          ) : (
            (approvalsData?.tasks || []).slice(0, 4).map((t) => (
              <ApprovalCard
                key={t.id}
                task={t}
                agent={{ name: t.assigned_agent || "Chief of Staff", department: "Product Studio" }}
                meta={
                  <>
                    <span>#{t.id.slice(-4)}</span>
                    <Chip value={t.priority} />
                    {t.assigned_agent && <span>· {t.assigned_agent}</span>}
                  </>
                }
                onApprove={() => trigger(t.id, "approve")}
                onChanges={() => trigger(t.id, "changes")}
                onReject={() => trigger(t.id, "reject")}
                onDetails={() => navigate(`/tasks/${t.id}`)}
                testId={`approval-${t.id}`}
              />
            ))
          )}
        </Panel>

        {/* Agents */}
        <Panel
          label="AGENTS"
          action={
            <DsButton variant="default" onClick={() => navigate("/floor")} data-testid="open-floor">
              Open Floor
            </DsButton>
          }
          testId="agents-panel"
          className="col-span-1"
        >
          <ul className="space-y-3" data-testid="agents-list">
            {agents.slice(0, 10).map((a) => (
              <li key={a.name} className="flex items-center gap-3">
                <AgentPortrait agent={a} size={34} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-ui text-sm font-semibold text-[color:var(--fg)] truncate">{a.name}</span>
                    <span className="font-mono text-[10px] text-[color:var(--muted)] uppercase">{a.department}</span>
                  </div>
                  <div className="text-xs text-[color:var(--muted)] truncate">
                    {a.latest_summary || a.current_task_title || "—"}
                  </div>
                </div>
                <StatusLight status={statusKey(a)} testId={`agent-status-${a.name}`} />
              </li>
            ))}
          </ul>
        </Panel>

        {/* Right column: Spend chart + Inbound + Running */}
        <div className="col-span-1 space-y-4">
          <Panel label="SPEND · 7 DAYS" action={<span className="font-mono text-[10.5px] text-[color:var(--muted)]">USD</span>} testId="spend-7d">
            <SpendChart data={spendSeries} />
          </Panel>

          <Panel
            label="INBOUND"
            action={
              <DsButton variant="default" onClick={() => navigate("/inbound")} data-testid="open-inbound">
                Open
              </DsButton>
            }
            testId="inbound-tile"
          >
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 font-mono text-xs">
              <span className="text-[color:var(--muted)] uppercase">NEW</span>
              <span className="text-[color:var(--fg)]">{counts.inbound_new ?? 0} request{counts.inbound_new === 1 ? "" : "s"}</span>
              <span className="text-[color:var(--muted)] uppercase">OPPORTUNITY</span>
              <span className="text-[color:var(--fg)]">{counts.opportunities_open ?? 0} open</span>
              <span className="text-[color:var(--muted)] uppercase">WATCHING</span>
              <span className="text-[color:var(--fg)]">{counts.opportunities_watching ?? 0}</span>
            </div>
          </Panel>

          <Panel label="RUNNING NOW" testId="running-now">
            {running.length === 0 ? (
              <div className="text-sm text-[color:var(--muted)]">Nothing in flight.</div>
            ) : (
              <ul className="space-y-2">
                {running.slice(0, 5).map((t) => (
                  <li
                    key={t.id}
                    className="grid grid-cols-[52px_1fr_auto] items-center gap-3 text-sm"
                  >
                    <Link
                      to={`/tasks/${t.id}`}
                      className="font-mono text-xs text-[color:var(--muted)] whitespace-nowrap tabular-nums hover:text-[color:var(--fg)]"
                      title={t.id}
                    >
                      T-{t.id.slice(-3)}
                    </Link>
                    <Link
                      to={`/tasks/${t.id}`}
                      className="min-w-0 truncate whitespace-nowrap text-[color:var(--fg)] hover:underline"
                      title={t.title}
                    >
                      {t.title}
                    </Link>
                    <Chip value={t.state} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>

      <DecisionDialog
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        taskId={activeId}
        decision={decision}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ["approvals"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
        }}
      />
    </div>
  );
}
